import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  AppBar,
  Toolbar,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  getSessions,
  StreamSession,
  StreamSessionStatus,
} from 'src/services/streamingService';
import { useStreamStart } from 'src/hooks/useStreamStart';
import LoaderError from 'src/components/LoaderError';
import { format } from 'date-fns';
import { useOmniverseApp } from '../../redux';
import { SolutionOmniverseApp } from '../../types';

/**
 * Page that displays active sessions for a specific app
 * Matches AppStreamList from ov-dgxc-portal-sample
 */
export default function AppSessionsList() {
  const { appId = '' } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  console.log('=== AppSessionsList Component ===');
  console.log('appId from useParams:', appId);

  // Get app data from Redux store
  const [omniverseApps, appsLoading] = useOmniverseApp();
  const app =
    omniverseApps?.find((a: SolutionOmniverseApp) => a.id === appId) || null;

  const [sessions, setSessions] = useState<StreamSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch active sessions for this app
  const fetchSessions = async () => {
    console.log('=== fetchSessions called ===');
    console.log('Params:', { appId });

    if (!appId) {
      console.log('❌ No appId available - skipping API call');
      setError('Missing app ID');
      setIsLoadingSessions(false);
      return;
    }

    try {
      setIsLoadingSessions(true);

      console.log('📡 Making getSessions API call with:', {
        appId,
        status: 'active',
      });

      // Get active sessions for this app
      const sessionPage = await getSessions({
        appId,
        status: 'active',
      });

      console.log('✅ Fetched sessions:', sessionPage);
      setSessions(sessionPage.items);
      setError(null);
    } catch (err) {
      console.error('❌ Failed to load sessions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setIsLoadingSessions(false);
    }
  };

  useEffect(() => {
    console.log('=== useEffect triggered ===');
    console.log('Params:', { appId });

    if (appId) {
      console.log('✅ appId available - calling fetchSessions');
      fetchSessions();
    } else {
      console.log('⏳ Waiting for appId...');
    }
  }, [appId]);

  // Get streamStart for creating new sessions
  const streamStart = useStreamStart({
    appId,
  });

  const handleStartNewSession = async () => {
    try {
      console.log('Starting new session...');
      console.log('Params:', { appId });
      const session = await streamStart.mutateAsync();
      console.log('Session created:', session);

      // Navigate to the streaming page
      navigate(`/app/${appId}/sessions/${session.id}`);
    } catch (err) {
      console.error('Failed to start session:', err);
    }
  };

  const handleReconnect = (sessionId: string) => {
    navigate(`/app/${appId}/sessions/${sessionId}`);
  };

  const handleClose = () => {
    window.close();
  };

  const getStatusColor = (status: StreamSessionStatus) => {
    switch (status) {
      case StreamSessionStatus.ACTIVE:
        return 'success';
      case StreamSessionStatus.CONNECTING:
        return 'info';
      case StreamSessionStatus.INITIALIZING:
        return 'warning';
      case StreamSessionStatus.FAILED:
        return 'error';
      default:
        return 'default';
    }
  };

  const calculateDuration = (startTime: Date, endTime?: Date) => {
    const end = endTime || new Date();
    const diffMs = end.getTime() - startTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `${diffMins}m`;
    }

    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  // Show loading screen while fetching app data
  if (appsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        <AppBar position="static" color="default" elevation={0}>
          <Toolbar>
            <Typography variant="h6">Loading...</Typography>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // Show error if app failed to load
  if (error && !app) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
        }}
      >
        <AppBar position="static" color="default" elevation={0}>
          <Toolbar>
            <Typography variant="h6">Error</Typography>
          </Toolbar>
        </AppBar>
        <LoaderError title="Failed to load application">{error}</LoaderError>
      </Box>
    );
  }

  return (
    <Dialog open fullScreen>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Sessions - {app?.title || 'Application'}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Active Sessions</Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSessions}
            disabled={isLoadingSessions}
          >
            Refresh
          </Button>
        </Box>

        {isLoadingSessions && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoadingSessions && error && (
          <LoaderError title="Failed to load sessions">{error}</LoaderError>
        )}
        {!isLoadingSessions && !error && sessions.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              You don't have any active sessions at the moment.
            </Typography>
          </Paper>
        )}
        {!isLoadingSessions && !error && sessions.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Session ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {session.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {session.userInfo?.userName || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={session.status}
                        color={getStatusColor(session.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {format(session.startTime, 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      {calculateDuration(session.startTime, session.endTime)}
                    </TableCell>
                    <TableCell align="right">
                      {session.status === StreamSessionStatus.INITIALIZING && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleReconnect(session.id)}
                        >
                          Reconnect
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleStartNewSession}
            disabled={streamStart.isPending}
          >
            New Session
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
